import { ONBOARDING_ROUTES } from "src/consts/routes";
import Text from "src/components/Displays/Text";
import { useCallback, useEffect, useState } from "react";
import { enphaseAssets } from "src/api/onboardingClients";
import ContentCard from "src/components/Shared/ContentCard";
import { useNavigate, useSearchParams } from "react-router-dom";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { CircularProgress, Divider, Stack } from "@mui/material";
import { EnphaseAssetsResponse } from "src/models/onboardingClientsModel";
import HorizontalLinearProgressBar from "src/components/ProgressBar/HorizontalLinearProgressBar";

const ConnectedDevices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") ?? "";
  const [assets, setAssets] = useState<EnphaseAssetsResponse>();
  const [isLoading, setIsLoading] = useState(false);

  const getAssets = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await enphaseAssets({ code });
      setAssets(response);
      setIsLoading(false);
    } catch (e) {
      setTimeout(() => {
        setIsLoading(false);
        navigate(ONBOARDING_ROUTES.ONBOARDING_ERROR_RETRY);
      }, 1000);
    }
  }, [code, navigate]);

  useEffect(() => {
    getAssets();
  }, [getAssets]);

  const handleToSystemOnboarding = () => {
    navigate(ONBOARDING_ROUTES.ONBOARDING_SYSTEM);
  };

  const microsLoaded =
    assets &&
    assets.devices &&
    assets.devices.micros &&
    assets.devices.micros.length > 0;

  const metersLoaded =
    assets &&
    assets.devices &&
    assets.devices.meters &&
    assets.devices.meters.length > 0;

  const gatewaysLoaded =
    assets &&
    assets.devices &&
    assets.devices.gateways &&
    assets.devices.gateways.length > 0;

  const enchargesLoaded =
    assets &&
    assets.devices &&
    assets.devices.encharges &&
    assets.devices.encharges.length > 0;

  return (
    <>
      <Stack alignItems={"center"} p={5}>
        <Stack
          sx={(theme) => ({
            padding: theme.spacing(3),
            width: { xs: "100%", sm: "500px" },
          })}
        >
          <Text
            text="Onboard your system"
            sx={(theme) => ({
              color: "#DDB1FF",
              fontWeight: "500",
              mb: theme.spacing(3),
            })}
          />
          <HorizontalLinearProgressBar variant="determinate" value={100} />
        </Stack>

        <ContentCard sx={{ width: { xs: "100%", sm: "500px" } }}>
          <Stack gap={5}>
            <Text
              text={`Assets of your system`}
              sx={(theme) => ({
                fontSize: "22px",
                lineHeight: "26px",
              })}
            />

            {isLoading && (
              <Stack alignItems={"center"}>
                <CircularProgress color="secondary" size={16} />
              </Stack>
            )}

            {!isLoading && (
              <Stack gap={5}>
                <Stack gap={2} sx={{ border: "1px solid lightgray", p: 2 }}>
                  <Text
                    text={`Micros: ${
                      microsLoaded ? assets.devices.micros.length : 0
                    }`}
                    sx={(theme) => ({ fontWeight: "bold" })}
                  />

                  {microsLoaded &&
                    assets.devices.micros.map((micro, index) => (
                      <Stack>
                        <Text
                          text={`Name: ${micro.name}`}
                          sx={{ fontWeight: 700 }}
                        />
                        <Text text={`Serial Number: ${micro.serial_number}`} />
                        <Text text={`Last import: ${micro.last_report_at}`} />

                        {assets.devices.micros.length !== index + 1 && (
                          <Divider sx={{ mt: 1 }} />
                        )}
                      </Stack>
                    ))}
                </Stack>

                <Stack gap={2} sx={{ border: "1px solid lightgray", p: 2 }}>
                  <Text
                    text={`Meters: ${
                      metersLoaded ? assets && assets.devices.meters.length : 0
                    }`}
                    sx={(theme) => ({ fontWeight: "bold" })}
                  />

                  {metersLoaded &&
                    assets.devices.meters.map((meter, index) => (
                      <Stack>
                        <Text
                          text={`Name: ${meter.name}`}
                          sx={{ fontWeight: 700 }}
                        />
                        <Text text={`Serial Number: ${meter.serial_number}`} />
                        <Text text={`Last import: ${meter.last_report_at}`} />
                        {assets.devices.meters.length !== index + 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Stack>
                    ))}
                </Stack>

                <Stack gap={2} sx={{ border: "1px solid lightgray", p: 2 }}>
                  <Text
                    text={`Gateways: ${
                      gatewaysLoaded ? assets.devices.gateways.length : 0
                    }`}
                    sx={(theme) => ({ fontWeight: "bold" })}
                  />

                  {gatewaysLoaded &&
                    assets.devices.gateways.map((gateway, index) => (
                      <Stack>
                        <Text
                          text={`Name: ${gateway.name}`}
                          sx={{ fontWeight: 700 }}
                        />
                        <Text
                          text={`Serial Number: ${gateway.serial_number}`}
                        />
                        <Text
                          text={`Emu Sw Version: ${gateway.emu_sw_version}`}
                        />
                        <Text text={`Last import: ${gateway.last_report_at}`} />

                        {assets.devices.gateways.length !== index + 1 && (
                          <Divider sx={{ mt: 1 }} />
                        )}
                      </Stack>
                    ))}
                </Stack>

                <Stack gap={2} sx={{ border: "1px solid lightgray", p: 2 }}>
                  <Text
                    text={`Encharges: ${
                      enchargesLoaded
                        ? assets && assets.devices.encharges.length
                        : 0
                    }`}
                    sx={(theme) => ({ fontWeight: "bold" })}
                  />

                  {enchargesLoaded &&
                    assets.devices.encharges.map((encharge, index) => (
                      <Stack>
                        <Text
                          text={`Name: ${encharge.name}`}
                          sx={{ fontWeight: 700 }}
                        />
                        <Text
                          text={`Serial Number: ${encharge.serial_number}`}
                        />
                        <Text
                          text={`Last import: ${encharge.last_report_at}`}
                        />
                        {assets.devices.encharges.length !== index + 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Stack>
                    ))}
                </Stack>
              </Stack>
            )}

            <Stack gap={3}>
              <PrimaryButton
                text={"Return to start"}
                onClick={handleToSystemOnboarding}
              />
            </Stack>
          </Stack>
        </ContentCard>
      </Stack>
    </>
  );
};

export default ConnectedDevices;
